import React, { useCallback, useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { Box, CircularProgress, Grid as MuiGrid, Stack, styled } from "@mui/material";
import { RoleComponentGuard } from "components/guards/Role-component.guard";
import { useActionHook, useDidUpdate, useModals, useRoleGuardHook } from "hooks";
import { pickBy } from "lodash";
import {
  useDealActions,
  useDealQueriesSelect,
  usePickUserPermissions,
  useTaskManageActions,
  useUserIdSelector,
  useUserRoleSelector
} from "store";
import { IDealPreview, IGetDealsData, UserPermissionType } from "store/types";

import { AddDealCard, GridCell } from "..";
import { RoleName } from "../../../constants";

const StyledMuiGrid = styled(MuiGrid)(({ theme }) => ({
  [theme.breakpoints.up(600)]: {
    rowGap: "40px",
    columnGap: "20px"
  },
  [theme.breakpoints.up(900)]: {
    rowGap: "40px",
    columnGap: "20px"
  },
  [theme.breakpoints.up(1200)]: {
    rowGap: "40px",
    columnGap: "20%"
  },
  [theme.breakpoints.up(1330)]: {
    rowGap: "25px",
    columnGap: "20px"
  },
  [theme.breakpoints.up(1360)]: {
    rowGap: "30px",
    columnGap: "30px"
  },
  [theme.breakpoints.up(1420)]: {
    rowGap: "40px",
    columnGap: "40px"
  },
  [theme.breakpoints.up(1536)]: {
    rowGap: "40px",
    columnGap: "80px"
  },
  [theme.breakpoints.up(1700)]: {
    rowGap: "50px",
    columnGap: "90px"
  },
  [theme.breakpoints.up(1800)]: {
    rowGap: "60px",
    columnGap: "60px"
  }
}));

interface GridProps {}

export const Grid: React.FC<GridProps> = () => {
  const [data, setData] = useState<IDealPreview[]>([]);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const { getDealsCardsAction, deleteDealAction } = useDealActions();
  const { getDealsAction } = useTaskManageActions();
  const { action: deleteDeal } = useActionHook(deleteDealAction, {
    onSuccess: getDealsAction
  });
  const dealQueries = useDealQueriesSelect();
  const { deal_title, order_by, filters } = dealQueries || {};
  const { deleteModal } = useModals();
  const currentUserId = useUserIdSelector();
  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;
  const role = useUserRoleSelector();

  const getDeal = useCallback(
    async (data: IGetDealsData & { turnOnLoading?: boolean }) => {
      try {
        if (data.turnOnLoading) {
          setLoading(true);
        }
        const { data: deals, last_page } = await getDealsCardsAction(data);
        if (data.page! >= last_page) {
          setHasMore(false);
        }

        setPage((prevState) => prevState + 1);
        setData((prevState) => [...prevState, ...deals]);
      } finally {
        if (data.turnOnLoading) {
          setLoading(false);
        }
      }
    },
    [setPage, hasMore]
  );

  const resetParams = useCallback(() => {
    setData([]);
    setPage(1);
    setLoading(false);
    setHasMore(true);
  }, []);

  // Initial API call
  useEffect(() => {
    const filtersPayload = pickBy(filters, (value) => value.length > 0);
    getDeal({
      filters: filtersPayload,
      deal_title,
      order_by,
      page: 1,
      turnOnLoading: true
    });
  }, []);

  // Listen queries, reset params and call api with default params
  useDidUpdate(() => {
    const filtersPayload = pickBy(filters, (value) => value.length > 0);
    resetParams();
    getDeal({
      filters: filtersPayload,
      deal_title,
      order_by,
      page: 1
    });
  }, [dealQueries]);

  const onClickDelete = useRoleGuardHook(
    (id: number, title: string) => {
      deleteModal({
        action: async () => {
          const dealId = await deleteDeal(id);
          setData(data.filter((deal: IDealPreview) => deal.id !== dealId));
        },
        element: "Delete",
        message: `Are you sure you want to delete ${title}?`
      });
    },
    [RoleName.Admin, RoleName.Analyst]
  );

  return (
    <Box overflow="hidden" width="100%">
      <InfiniteScroll
        loader={
          <Stack height="100%" justifyContent="center" alignItems="center">
            <CircularProgress size={150} />
          </Stack>
        }
        next={() =>
          getDeal({
            deal_title,
            order_by,
            page,
            filters: filters
          })
        }
        height="100%"
        hasMore={hasMore}
        dataLength={data.length}
      >
        {!loading && (
          <StyledMuiGrid container direction="row" flexGrow="1" columns={3}>
            <RoleComponentGuard
              availableRoles={[RoleName.Admin, RoleName.Analyst]}
              hideChild={isViewerPermission || noAccessPermission}
            >
              <MuiGrid item>
                <AddDealCard />
              </MuiGrid>
            </RoleComponentGuard>
            {data.map((dealPreview: IDealPreview, index) => (
              <MuiGrid item key={index}>
                <GridCell
                  key={index}
                  {...(onClickDelete &&
                    (currentUserId === dealPreview.user_id || role?.name === RoleName.Admin) && { onClickDelete })}
                  {...dealPreview}
                />
              </MuiGrid>
            ))}
          </StyledMuiGrid>
        )}
      </InfiniteScroll>
    </Box>
  );
};
